/**
 * Exponential Backoff Utility
 *
 * Implements exponential backoff with jitter for WebSocket reconnection.
 * Prevents thundering herd problem by adding randomness to delays.
 */

export interface BackoffConfig {
  /** Starting delay in milliseconds (default: 1000) */
  initialDelayMs: number;
  /** Maximum delay cap in milliseconds (default: 60000) */
  maxDelayMs: number;
  /** Growth factor for exponential increase (default: 2) */
  multiplier: number;
  /** Random variance factor 0-1 (default: 0.3 for +/-30%) */
  jitterFactor: number;
  /** Maximum attempts before failing (default: 10) */
  maxRetries: number;
}

const DEFAULT_CONFIG: BackoffConfig = {
  initialDelayMs: 1000,
  maxDelayMs: 60000,
  multiplier: 2,
  jitterFactor: 0.3,
  maxRetries: 10,
};

/**
 * Exponential backoff calculator with jitter support
 *
 * @example
 * const backoff = new ExponentialBackoff();
 *
 * // Get delays: 1s, 2s, 4s, 8s, 16s, 32s, 60s (capped)
 * while (backoff.hasRetriesRemaining()) {
 *   const delay = backoff.getNextDelay();
 *   backoff.recordAttempt();
 *   await wait(delay);
 *   // Try to connect...
 * }
 */
export class ExponentialBackoff {
  private attempt: number = 0;
  private config: BackoffConfig;

  constructor(config: Partial<BackoffConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Calculate the next delay with jitter
   * Formula: min(initialDelay * multiplier^attempt, maxDelay) +/- jitter
   */
  getNextDelay(): number {
    const baseDelay = Math.min(
      this.config.initialDelayMs *
        Math.pow(this.config.multiplier, this.attempt),
      this.config.maxDelayMs,
    );

    // Add jitter to prevent thundering herd
    // Jitter range: -jitterFactor to +jitterFactor of base delay
    const jitter =
      baseDelay * this.config.jitterFactor * (Math.random() * 2 - 1);

    return Math.max(0, Math.floor(baseDelay + jitter));
  }

  /**
   * Record that an attempt was made
   */
  recordAttempt(): void {
    this.attempt++;
  }

  /**
   * Reset the backoff state (on successful connection)
   */
  reset(): void {
    this.attempt = 0;
  }

  /**
   * Check if we have retries remaining
   */
  hasRetriesRemaining(): boolean {
    return this.attempt < this.config.maxRetries;
  }

  /**
   * Get current attempt number (0-based)
   */
  get currentAttempt(): number {
    return this.attempt;
  }

  /**
   * Get maximum retry limit
   */
  get maxRetries(): number {
    return this.config.maxRetries;
  }

  /**
   * Get remaining retry attempts
   */
  get remainingRetries(): number {
    return Math.max(0, this.config.maxRetries - this.attempt);
  }

  /**
   * Get estimated time until all retries exhausted (rough estimate)
   */
  getEstimatedTotalTime(): number {
    let total = 0;
    for (let i = 0; i < this.config.maxRetries; i++) {
      const baseDelay = Math.min(
        this.config.initialDelayMs * Math.pow(this.config.multiplier, i),
        this.config.maxDelayMs,
      );
      total += baseDelay;
    }
    return total;
  }

  /**
   * Create a new backoff instance with custom config
   */
  static create(config: Partial<BackoffConfig> = {}): ExponentialBackoff {
    return new ExponentialBackoff(config);
  }

  /**
   * Get formatted delay for display (e.g., "5.2s")
   */
  getFormattedDelay(): string {
    const delay = this.getNextDelay();
    if (delay < 1000) {
      return `${delay}ms`;
    }
    return `${(delay / 1000).toFixed(1)}s`;
  }
}

/**
 * Simple delay utility for async/await
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
